import React, { useState, useEffect } from 'react';
import Sidebar from '../Sidebar';
import { WizardProgress } from '../wizard/WizardComponents';

// ── Validation rules per ID type ──────────────────────────────
const VALIDATORS = {
  aadharNumber:         { regex: /^\d{12}$/,                   msg: 'Aadhar must be exactly 12 digits' },
  drivingLicenceNumber: { regex: /^[A-Z]{2}\d{2}\s?\d{4}\d{7}$/i, msg: 'Enter a valid Driving Licence No. (e.g. GJ012023XXXXXXX)' },
  passportNumber:       { regex: /^[A-Z]\d{7}$/i,             msg: 'Passport must be 1 letter + 7 digits (e.g. A1234567)' },
  panNumber:            { regex: /^[A-Z]{5}\d{4}[A-Z]$/i,     msg: 'PAN must be 10 chars (e.g. ABCDE1234F)' },
};

const validate = (key, value) => {
  const v = value.trim();
  if (!v) return '';                         // empty = no error (optional fields)
  const rule = VALIDATORS[key];
  if (rule && !rule.regex.test(v)) return rule.msg;
  return '';
};

// ── Upload side (front or back) ───────────────────────────────
const UploadSide = ({ side, file, onUpload, hasError }) => (
  <div style={{ flex: 1 }}>
    <p style={{ fontSize: 11, fontWeight: 700, color: hasError ? '#E85D4A' : '#6B6B6B', textTransform: 'uppercase', letterSpacing: 0.8, margin: '0 0 6px' }}>
      {side === 'front' ? '⬆ Front Side' : '⬇ Back Side'}{hasError ? ' *' : ''}
    </p>
    <label style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
      padding: '10px 0', background: file ? '#EDF7F2' : '#F4F1ED',
      border: `2px dashed ${file ? '#22a06b' : hasError ? '#E85D4A' : '#C8C2BB'}`,
      borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600,
      color: file ? '#22a06b' : hasError ? '#E85D4A' : '#1B3B5F', userSelect: 'none',
      transition: 'all .2s', minHeight: 44, width: '100%', boxSizing: 'border-box',
    }}>
      {file ? `✓ ${file.name.length > 18 ? file.name.slice(0, 16) + '…' : file.name}` : `📎 ${side === 'front' ? 'Front' : 'Back'}`}
      <input type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }}
        onChange={e => e.target.files[0] && onUpload(e.target.files[0])} />
    </label>
    {hasError && <p style={{ fontSize: 11, color: '#E85D4A', margin: '4px 0 0', fontWeight: 600 }}>Required</p>}
  </div>
);

// ── Single ID proof card ──────────────────────────────────────
const IdProofCard = ({ label, numberKey, frontKey, backKey, placeholder, maxLength, docs, fieldErrors, setField, isComplete, touched }) => {
  const numErr  = touched[numberKey]  ? fieldErrors[numberKey]  : '';
  const frontErr = touched[frontKey]  ? fieldErrors[frontKey]   : '';
  const backErr  = touched[backKey]   ? fieldErrors[backKey]    : '';

  return (
    <div style={{
      marginBottom: 20, padding: 16,
      border: `1px solid ${isComplete ? '#22a06b' : (numErr || frontErr || backErr) ? '#E85D4A' : '#E8E4DF'}`,
      borderRadius: 8, background: isComplete ? '#f0faf5' : '#FAFAF9', transition: 'all .2s',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: '#1B3B5F' }}>{label}</span>
        {isComplete && <span style={{ fontSize: 13, color: '#22a06b', fontWeight: 700 }}>✓ Complete</span>}
      </div>

      {/* Number input */}
      <input
        style={{
          width: '100%', padding: '9px 12px',
          border: `1px solid ${numErr ? '#E85D4A' : '#E8E4DF'}`,
          borderRadius: 4, fontSize: 14, outline: 'none', fontFamily: 'inherit',
          color: '#2C2C2C', background: '#fff', boxSizing: 'border-box', marginBottom: numErr ? 4 : 12,
        }}
        placeholder={placeholder}
        value={docs[numberKey]}
        maxLength={maxLength}
        onChange={e => setField(numberKey, e.target.value)}
        onBlur={() => setField('__touch__', numberKey)}
      />
      {numErr && <p style={{ fontSize: 11, color: '#E85D4A', margin: '0 0 10px', fontWeight: 600 }}>⚠ {numErr}</p>}

      {/* Front / Back upload */}
      <div style={{ display: 'flex', gap: 12 }}>
        <UploadSide side="front" file={docs[frontKey]}  hasError={!!frontErr}
          onUpload={f => setField(frontKey, f)} />
        <UploadSide side="back"  file={docs[backKey]}   hasError={!!backErr}
          onUpload={f => setField(backKey, f)} />
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────
const RegistrationStep3 = ({ onNavigate, onLogout, onDocsSelected  }) => {
  const [docs, setDocs] = useState({
    aadharNumber: '',        aadharFront: null,         aadharBack: null,
    drivingLicenceNumber: '', drivingLicenceFront: null, drivingLicenceBack: null,
    passportNumber: '',      passportFront: null,        passportBack: null,
    panNumber: '',           panFront: null,             panBack: null,
    lightBillFile: null,
  });

  // touched tracks which fields the user has interacted with
  const [touched, setTouched] = useState({});
  const [submitErr, setSubmitErr] = useState('');

  useEffect(() => {
    const saved = localStorage.getItem('registrationStep3Data');
    if (saved) {
      try {
        const p = JSON.parse(saved);
        setDocs(prev => ({
          ...prev,
          aadharNumber:         p.aadharNumber         || '',
          drivingLicenceNumber: p.drivingLicenceNumber || '',
          passportNumber:       p.passportNumber       || '',
          panNumber:            p.panNumber            || '',
        }));
      } catch (e) {}
    }
  }, []);

  // setField handles both normal fields and the __touch__ sentinel
  const setField = (key, val) => {
    if (key === '__touch__') {
      setTouched(p => ({ ...p, [val]: true }));
      return;
    }
    setDocs(p => ({ ...p, [key]: val }));
    setTouched(p => ({ ...p, [key]: true }));
    setSubmitErr('');
  };

  // ── Compute per-field errors (live) ──────────────────────────
  const fieldErrors = {};

  // Number format errors
  ['aadharNumber', 'drivingLicenceNumber', 'passportNumber', 'panNumber'].forEach(k => {
    const err = validate(k, docs[k]);
    if (err) fieldErrors[k] = err;
  });

  // If a number is entered, front+back become required for that ID type
  const requireDocs = (numKey, frontKey, backKey) => {
    if (docs[numKey].trim()) {
      if (!docs[frontKey]) fieldErrors[frontKey] = 'Front side required';
      if (!docs[backKey])  fieldErrors[backKey]  = 'Back side required';
    }
  };
  requireDocs('aadharNumber',         'aadharFront',        'aadharBack');
  requireDocs('drivingLicenceNumber', 'drivingLicenceFront','drivingLicenceBack');
  requireDocs('passportNumber',       'passportFront',      'passportBack');
  requireDocs('panNumber',            'panFront',           'panBack');

  // Completion flags
  const aadharComplete   = !!(docs.aadharNumber.trim()         && !validate('aadharNumber', docs.aadharNumber)         && docs.aadharFront  && docs.aadharBack);
  const dlComplete       = !!(docs.drivingLicenceNumber.trim() && !validate('drivingLicenceNumber', docs.drivingLicenceNumber) && docs.drivingLicenceFront && docs.drivingLicenceBack);
  const passportComplete = !!(docs.passportNumber.trim()       && !validate('passportNumber', docs.passportNumber)       && docs.passportFront && docs.passportBack);
  const panComplete      = !!(docs.panNumber.trim()            && !validate('panNumber', docs.panNumber)                 && docs.panFront      && docs.panBack);

  const handleNext = () => {
    // Touch all fields to reveal all errors
    const allKeys = ['aadharNumber','aadharFront','aadharBack','drivingLicenceNumber','drivingLicenceFront','drivingLicenceBack','passportNumber','passportFront','passportBack','panNumber','panFront','panBack','lightBillFile'];
    setTouched(allKeys.reduce((acc, k) => ({ ...acc, [k]: true }), {}));

    // ── Size guard ──────────────────────────────────────────────────────
    // Documents upload together in one request; the server caps the total.
    // Reject an oversized single file, and an oversized combined batch, with
    // a clear message rather than a silent upload failure later.
    const docFilesForSize = [
      docs.aadharFront, docs.aadharBack,
      docs.drivingLicenceFront, docs.drivingLicenceBack,
      docs.passportFront, docs.passportBack,
      docs.panFront, docs.panBack,
      docs.lightBillFile,
    ].filter(Boolean);

    const MB = 1024 * 1024;
    const tooBig = docFilesForSize.find(f => f.size > 15 * MB);
    if (tooBig) {
      setSubmitErr(`"${tooBig.name}" is over 15 MB. Please upload a smaller image or a PDF.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    const totalMB = docFilesForSize.reduce((s, f) => s + f.size, 0) / MB;
    if (totalMB > 55) {
      setSubmitErr(`Your documents total ${totalMB.toFixed(1)} MB, which is too large to upload together. Please use smaller images or PDF files (tip: on iPhone, use "Most Compatible" format).`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    if (!(aadharComplete || dlComplete || passportComplete || panComplete)) {
      setSubmitErr('Please complete at least one ID proof — valid number + both front and back sides');
      return;
    }
    if (!docs.lightBillFile) {
      setSubmitErr('Please upload Light Bill as residential proof');
      return;
    }
    // Check no partial/invalid entries
    const hasFormatErr = Object.keys(fieldErrors).some(k =>
      ['aadharNumber','drivingLicenceNumber','passportNumber','panNumber'].includes(k)
    );
    if (hasFormatErr) {
      setSubmitErr('Please fix the errors above before continuing');
      return;
    }
    setSubmitErr('');

    localStorage.setItem('registrationStep3Data', JSON.stringify({
      aadharNumber:         docs.aadharNumber,
      drivingLicenceNumber: docs.drivingLicenceNumber,
      passportNumber:       docs.passportNumber,
      panNumber:            docs.panNumber,
    }));

   const selectedDocFiles = {
      aadharFront:         docs.aadharFront          || null,
      aadharBack:          docs.aadharBack            || null,
      drivingLicenseFront: docs.drivingLicenceFront  || null,
      drivingLicenseBack:  docs.drivingLicenceBack   || null,
      passportFront:       docs.passportFront         || null,
      passportBack:        docs.passportBack          || null,
      panFront:            docs.panFront              || null,
      panBack:             docs.panBack               || null,
      lightBill:           docs.lightBillFile         || null,
      // legacy keys
      aadhar:              docs.aadharFront           || null,
      drivingLicense:      docs.drivingLicenceFront   || null,
      passport:            docs.passportFront         || null,
      pan:                 docs.panFront              || null,
    };
    window.selectedDocFiles = selectedDocFiles;
    if (onDocsSelected) onDocsSelected(selectedDocFiles);  // ✅ persist in parent state (survives reload/navigation)

    onNavigate('registration4');
  }

  return (
    <div className="app-layout">
      <Sidebar activeItem="registration" onNavigate={onNavigate} onLogout={onLogout} />
      <main className="main-content">
        <div className="top-bar"><h1 className="page-title">Document Upload</h1></div>
        <div className="content-area">
          <div className="wizard-container">
            <WizardProgress currentStep={3} />
            <div className="wizard-card" style={{ padding: 40 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 60px' }}>

                {/* ── Left: ID Proof ── */}
                <div>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#1B3B5F', marginBottom: 4 }}>ID Proof</p>
              <p style={{ fontSize: 12, color: '#E85D4A', marginBottom: 6, marginTop: 0 }}>
                * Complete at least one ID proof (valid number + front + back)
              </p>
              <p style={{ fontSize: 11, color: '#6B6B6B', marginBottom: 16, marginTop: 0 }}>
                📎 JPG, PNG or PDF · up to 15 MB each. Tip: on iPhone use "Most Compatible" format.
              </p>

                  <IdProofCard label="Aadhar Card"
                    numberKey="aadharNumber" frontKey="aadharFront" backKey="aadharBack"
                    placeholder="Enter 12-digit Aadhar number" maxLength={12}
                    docs={docs} fieldErrors={fieldErrors} touched={touched}
                    setField={setField} isComplete={aadharComplete} />

                  <IdProofCard label="Driving Licence"
                    numberKey="drivingLicenceNumber" frontKey="drivingLicenceFront" backKey="drivingLicenceBack"
                    placeholder="Enter Driving Licence No. (e.g. GJ012023XXXXXXX)"
                    docs={docs} fieldErrors={fieldErrors} touched={touched}
                    setField={setField} isComplete={dlComplete} />

                  <IdProofCard label="Passport"
                    numberKey="passportNumber" frontKey="passportFront" backKey="passportBack"
                    placeholder="Enter Passport No. (e.g. A1234567)"
                    docs={docs} fieldErrors={fieldErrors} touched={touched}
                    setField={setField} isComplete={passportComplete} />

                  <IdProofCard label="PAN Card"
                    numberKey="panNumber" frontKey="panFront" backKey="panBack"
                    placeholder="Enter PAN No. (e.g. ABCDE1234F)" maxLength={10}
                    docs={docs} fieldErrors={fieldErrors} touched={touched}
                    setField={setField} isComplete={panComplete} />
                </div>

                {/* ── Right: Residential Proof ── */}
                <div>
                  <p style={{ fontSize: 14, fontWeight: 700, color: '#1B3B5F', marginBottom: 4 }}>Residential Proof</p>
                  <p style={{ fontSize: 12, color: '#E85D4A', marginBottom: 16, marginTop: 0 }}>* Light Bill is mandatory</p>

                  <div style={{ padding: 20, border: `1px solid ${docs.lightBillFile ? '#22a06b' : (touched.lightBillFile && !docs.lightBillFile) ? '#E85D4A' : '#E8E4DF'}`, borderRadius: 6, background: docs.lightBillFile ? '#f0faf5' : '#FAFAF9', minHeight: 100 }}>
                    <p style={{ fontSize: 13, color: '#6B6B6B', marginBottom: 6, marginTop: 0 }}>
                  Upload your latest electricity (light) bill as proof of residence.
                </p>
                <p style={{ fontSize: 11, color: '#6B6B6B', marginBottom: 14, marginTop: 0 }}>
                  📎 JPG, PNG or PDF · up to 15 MB.
                </p>
                    <label style={{
                      display: 'inline-flex', alignItems: 'center', gap: 8,
                      padding: '8px 18px', background: '#1B3B5F', color: '#fff',
                      borderRadius: 4, cursor: 'pointer', fontSize: 13, fontWeight: 600, userSelect: 'none',
                    }}>
                      📎 Upload Light Bill
                      <input type="file" accept=".jpg,.jpeg,.png,.pdf" style={{ display: 'none' }}
                        onChange={e => { if (e.target.files[0]) { setField('lightBillFile', e.target.files[0]); } }} />
                    </label>
                    {docs.lightBillFile
                      ? <p style={{ fontSize: 12, color: '#22a06b', marginTop: 10, marginBottom: 0, fontWeight: 600 }}>✓ {docs.lightBillFile.name} uploaded successfully</p>
                      : touched.lightBillFile && <p style={{ fontSize: 11, color: '#E85D4A', marginTop: 8, marginBottom: 0, fontWeight: 600 }}>⚠ Light Bill is required</p>
                    }
                  </div>
                </div>

              </div>

              {/* Submit-level error */}
              {submitErr && (
                <div style={{ background: '#FFF3F3', border: '1px solid #E85D4A', borderLeft: '4px solid #E85D4A', borderRadius: 4, padding: '10px 14px', marginTop: 20, color: '#9e2a2a', fontSize: 13 }}>
                  ⚠️ {submitErr}
                </div>
              )}

              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 12, marginTop: 40, paddingTop: 20, borderTop: '1px solid #E8E4DF' }}>
                <button className="btn-back" onClick={() => onNavigate('registration2', { isBack: true })}>Back</button>
                <button className="btn-submit" onClick={handleNext}>Next</button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegistrationStep3;